"use client";

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { AlertCircle, ArrowRight, Check, CheckCircle2, ChevronDown, Eye, EyeOff, Loader2, Menu, Search, ShieldCheck, User, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, ProductPrice, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ProductsSearch, buildProductSearchUrl, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { useWooAccountUrl } from "@/integrations/wordpress/useWooAccountUrl";
import { WordPressUserAuth } from "@/integrations/wordpress/WordPressUserAuth";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { getProductImageAlt, useProductImageAlts } from "@/lib/productImageUtils";
import { setAccessGateState } from "@/hooks/useAccessGate";
import { isLiveSite, getBuildYourPackLinkProps } from "@/lib/pageLinks";
import HeaderCart from "./HeaderCart";
import "./Header.css";
const auth = new WordPressUserAuth();
const TEK_TIDES_LOGO = "https://tektideslabs.com/wp-content/uploads/2026/10/TEK-TIDES-LABS-Wave-Logo-1.png";
function safeLocalStorageGet(key: string) {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeLocalStorageSet(key: string, value: string) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Site remains usable if browser storage is unavailable.
  }
}
function safeSessionStorageGet(key: string) {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSessionStorageSet(key: string, value: string) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Site remains usable if session storage is unavailable.
  }
}
function HeaderWaveAccent() {
  const shouldReduceMotion = useReducedMotion();
  return <div className="pointer-events-none absolute bottom-0 left-0 z-0 w-full overflow-hidden leading-none" aria-hidden="true">
      <motion.div className="flex w-[200%]" animate={shouldReduceMotion ? undefined : {
      x: ["0%", "-50%"]
    }} transition={shouldReduceMotion ? undefined : {
      duration: 14,
      ease: "linear",
      repeat: Infinity
    }}>
        {[0, 1].map(wave => <svg key={wave} className="h-2.5 w-full shrink-0 text-primary/30 sm:h-3" viewBox="0 0 1200 24" fill="none" preserveAspectRatio="none">
            <path d="M0,12 C150,24 350,0 600,12 C850,24 1050,0 1200,12 L1200,24 L0,24 Z" fill="currentColor" fillOpacity="0.22" />
            <path d="M0,12 C150,24 350,0 600,12 C850,24 1050,0 1200,12" stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.5" />
          </svg>)}
      </motion.div>
    </div>;
}
function cleanHost(hostname: string): string {
  return hostname.toLowerCase().replace(/^www\./, "");
}
function getSafeOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "http://localhost";
}
function isLiveSiteHref(href: string): boolean {
  if (typeof window === "undefined") {
    return true; // SSR safe: treat as live
  }
  try {
    const parsed = new URL(href, getSafeOrigin());
    return cleanHost(parsed.hostname) === cleanHost(window.location.hostname);
  } catch {
    return false;
  }
}
function isBuildYourPackItem(item: {
  href?: string;
  label?: string;
}): boolean {
  const label = (item.label || "").toLowerCase();
  if (label.includes("build your pack")) return true;
  if (!item.href) return false;
  try {
    const parsed = new URL(item.href, getSafeOrigin());
    if (parsed.searchParams.get("page_id") === "605") return true;
    const pathname = parsed.pathname.toLowerCase().replace(/\/+$/, "") || "/";
    return pathname === "/build-your-pack" || pathname === "/buildyourpackpage";
  } catch {
    const lower = item.href.toLowerCase();
    return lower.includes("page_id=605") || lower.includes("build-your-pack") || lower.includes("buildyourpackpage");
  }
}
function extractHash(rawHref?: string): string {
  if (!rawHref) return "";
  try {
    return new URL(rawHref, getSafeOrigin()).hash || "";
  } catch {
    const hashIdx = rawHref.indexOf("#");
    return hashIdx !== -1 ? rawHref.slice(hashIdx) : "";
  }
}
function mapFallbackLabel(label?: string): ResolvedMenuLink {
  const clean = (label || "").trim().toLowerCase();
  const live = isLiveSite();
  if (clean === "home") {
    return live ? {
      href: "/"
    } : {
      to: "/"
    };
  }
  if (clean === "shop" || clean === "research products") {
    return live ? {
      href: "/research-products/"
    } : {
      to: "/ShopPage"
    };
  }
  if (clean.includes("pack")) {
    return getBuildYourPackLinkProps();
  }
  if (clean.includes("quality") || clean.includes("coa")) {
    return live ? {
      href: "/quality-coas/"
    } : {
      to: "/QualityTestingPage"
    };
  }
  if (clean === "about") {
    return live ? {
      href: "/about/"
    } : {
      to: "/AboutPage"
    };
  }
  if (clean === "faq") {
    return live ? {
      href: "/faq/"
    } : {
      to: "/FaqPage"
    };
  }
  if (clean === "contact") {
    return live ? {
      href: "/contact/"
    } : {
      to: "/ContactPage"
    };
  }
  if (clean === "checkout") {
    return live ? {
      href: "/checkout/"
    } : {
      to: "/CheckoutPage"
    };
  }
  if (clean.includes("account")) {
    return live ? {
      href: "/my-account/"
    } : {
      to: "/MyAccountPage"
    };
  }
  if (clean.includes("policy") || clean.includes("policies")) {
    return live ? {
      href: "/policies/"
    } : {
      to: "/PoliciesPage"
    };
  }
  if (clean === "cart") {
    return live ? {
      href: "/cart/"
    } : {
      to: "/CartPage"
    };
  }
  return live ? {
    href: "/research-products/"
  } : {
    to: "/ShopPage"
  };
}
function mapHrefToPreviewRoute(href: string): string | null {
  try {
    const parsed = new URL(href, getSafeOrigin());
    const pathname = parsed.pathname.toLowerCase().replace(/\/+$/, "") || "/";
    const searchParams = parsed.searchParams;
    const pageId = searchParams.get("page_id");
    if (pageId === "605" || pathname === "/build-your-pack" || pathname === "/buildyourpackpage") {
      return "/BuildYourPackPage";
    }
    if (pageId === "20" || pathname === "/home" || pathname === "/homepage") {
      return parsed.hash ? `/${parsed.hash}` : "/";
    }
    if (pageId === "21" || pathname === "/shop" || pathname === "/shoppage" || pathname === "/research-products") {
      return "/ShopPage";
    }
    if (pageId === "23" || pathname === "/qualitytestingpage" || pathname === "/quality-testing" || pathname === "/quality-coas") {
      return "/QualityTestingPage";
    }
    if (pageId === "25" || pathname === "/about" || pathname === "/aboutpage") {
      return "/AboutPage";
    }
    if (pageId === "27" || pathname === "/faq" || pathname === "/faqpage") {
      return "/FaqPage";
    }
    if (pageId === "29" || pathname === "/contact" || pathname === "/contactpage") {
      return "/ContactPage";
    }
    if (pageId === "31" || pathname === "/checkout" || pathname === "/checkoutpage") {
      return "/CheckoutPage";
    }
    if (pageId === "32" || pathname === "/my-account" || pathname === "/myaccountpage") {
      return "/MyAccountPage";
    }
    if (pageId === "39" || pathname === "/policies" || pathname === "/policiespage") {
      return `/PoliciesPage${parsed.hash || ""}`;
    }
    if (pageId === "199" || pathname === "/cart" || pathname === "/cartpage") {
      return "/CartPage";
    }
  } catch {
    // URL parsing failed
  }
  return null;
}
export interface ResolvedMenuLink {
  to?: string;
  href?: string;
}
export function resolveMenuLink(item: {
  href?: string;
  label?: string;
}): ResolvedMenuLink {
  const isSSR = typeof window === "undefined";
  const hasValidHref = Boolean(item.href && item.href !== "#");
  if (!hasValidHref) {
    return mapFallbackLabel(item.label);
  }
  const rawHref = item.href as string;
  const isPack = isBuildYourPackItem(item);

  // Check if href is relative (does not start with a scheme or //)
  const isRelative = !/^[a-z][a-z0-9+.-]*:/i.test(rawHref) && !rawHref.startsWith("//");
  if (isRelative) {
    if (isPack) {
      return getBuildYourPackLinkProps(extractHash(rawHref));
    }
    if (isLiveSite()) {
      return {
        href: rawHref
      };
    }
    const previewRoute = mapHrefToPreviewRoute(rawHref);
    if (previewRoute) {
      return {
        to: previewRoute
      };
    }
    return {
      href: rawHref
    };
  }

  // Absolute href handling:
  if (isSSR || isLiveSiteHref(rawHref)) {
    if (isPack) {
      return getBuildYourPackLinkProps(extractHash(rawHref));
    }
    return {
      href: rawHref
    };
  }

  // Running in preview sandbox for mismatched/external host hrefs
  if (isPack) {
    return getBuildYourPackLinkProps(extractHash(rawHref));
  }
  const previewRoute = mapHrefToPreviewRoute(rawHref);
  if (previewRoute) {
    return {
      to: previewRoute
    };
  }
  return {
    href: rawHref
  };
}
interface FallbackMenuItem {
  label: string;
  linkProps: ResolvedMenuLink;
}
function getFallbackMenuItems(): FallbackMenuItem[] {
  const live = isLiveSite();
  return [{
    label: "Home",
    linkProps: live ? {
      href: "/"
    } : {
      to: "/"
    }
  }, {
    label: "Shop",
    linkProps: live ? {
      href: "/research-products/"
    } : {
      to: "/ShopPage"
    }
  }, {
    label: "Build your pack",
    linkProps: getBuildYourPackLinkProps()
  }, {
    label: "FAQ",
    linkProps: live ? {
      href: "/faq/"
    } : {
      to: "/FaqPage"
    }
  }, {
    label: "Contact",
    linkProps: live ? {
      href: "/contact/"
    } : {
      to: "/ContactPage"
    }
  }];
}

/**
 * Ensures 'Build your pack' comes immediately after 'Shop' in the rendered items,
 * if both exist in the menu list or if Build your pack needs to be inserted.
 */
function orderMenuItems(items: MenuItem[]): MenuItem[] {
  if (!items || items.length === 0) return items;
  const isShopItem = (item: MenuItem) => {
    const label = (item.label || "").toLowerCase();
    if (label === "shop" || label === "research products") return true;
    const href = (item.href || "").toLowerCase();
    return href.includes("/shop") || href.includes("page_id=21") || href.includes("/research-products");
  };
  const isPackItem = (item: MenuItem) => isBuildYourPackItem(item);
  const shopIndex = items.findIndex(isShopItem);
  const packIndex = items.findIndex(isPackItem);
  if (shopIndex === -1 && packIndex === -1) {
    return items;
  }
  const result = [...items];

  // If pack item exists in menu, move it right after Shop
  if (packIndex > -1 && shopIndex > -1) {
    const [packItem] = result.splice(packIndex, 1);
    const newShopIndex = result.findIndex(isShopItem);
    result.splice(newShopIndex + 1, 0, packItem);
    return result;
  }

  // If Shop exists but pack item is missing from WordPress Header Menu, insert it right after Shop
  if (shopIndex > -1 && packIndex === -1) {
    const packLink = getBuildYourPackLinkProps();
    const packItem: MenuItem = {
      id: "menu-item-build-your-pack",
      label: "Build your pack",
      href: packLink.href || packLink.to || "/?page_id=605"
    };
    result.splice(shopIndex + 1, 0, packItem);
    return result;
  }
  return result;
}
function RecursiveSubmenu({
  items
}: {
  items: MenuItem[];
}) {
  return <ul className="grid min-w-[210px] gap-1 rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-xl">
      {items.map((child, index) => {
      const linkProps = resolveMenuLink(child);
      return <li key={child.id || `${child.label}-${index}`} className="space-y-1">
            <NavigationMenuLink asChild>
              <Link {...linkProps} className="flex items-center justify-between rounded-sm px-3 py-2 text-sm font-medium text-popover-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                <span>{child.label}</span>
                {child.children && child.children.length > 0 && <ChevronDown className="size-3 -rotate-90 text-muted-foreground" aria-hidden="true" />}
              </Link>
            </NavigationMenuLink>
            {child.children && child.children.length > 0 && <div className="border-l border-border/60 pl-3">
                <RecursiveSubmenu items={child.children} />
              </div>}
          </li>;
    })}
    </ul>;
}
function DesktopFallbackMenu() {
  const items = getFallbackMenuItems();
  return <nav aria-label="Primary navigation" className="flex items-center gap-1">
      {items.map(item => <Link key={item.label} {...item.linkProps} className="rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          {item.label}
        </Link>)}
    </nav>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2" aria-label="Loading navigation" aria-busy="true">
        <div className="h-8 w-20 animate-pulse rounded-md bg-muted/60" />
        <div className="h-8 w-24 animate-pulse rounded-md bg-muted/60" />
        <div className="h-8 w-20 animate-pulse rounded-md bg-muted/60" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <DesktopFallbackMenu />;
  }
  const orderedItems = orderMenuItems(menuItems);
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {orderedItems.map((item, index) => {
        const linkProps = resolveMenuLink(item);
        return <NavigationMenuItem key={item.id || `${item.label}-${index}`}>
              {item.children && item.children.length > 0 ? <>
                  <NavigationMenuTrigger className="bg-transparent text-sm font-medium tracking-tight text-foreground hover:bg-secondary/60 hover:text-foreground data-[state=open]:bg-secondary/80">
                    {item.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <RecursiveSubmenu items={item.children} />
                  </NavigationMenuContent>
                </> : <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
                  <Link {...linkProps} className="bg-transparent text-sm font-medium tracking-tight text-foreground transition-colors hover:bg-secondary/60 hover:text-foreground">
                    {item.label}
                  </Link>
                </NavigationMenuLink>}
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileRecursiveMenu({
  items,
  depth = 0,
  onNavigate
}: {
  items: MenuItem[];
  depth?: number;
  onNavigate: () => void;
}) {
  return <ul className={depth === 0 ? "flex w-full flex-col gap-1" : "mt-1 flex flex-col gap-1 border-l border-[#dce6ea] pl-4"}>
      {items.map((item, index) => {
      const linkProps = resolveMenuLink(item);
      return <li key={item.id || `${item.label}-${depth}-${index}`} className="flex flex-col">
            <Link {...linkProps} onClick={onNavigate} className="tt-drawer-nav-link">
              <span>{item.label}</span>
            </Link>
            {item.children && item.children.length > 0 && <MobileRecursiveMenu items={item.children} depth={depth + 1} onNavigate={onNavigate} />}
          </li>;
    })}
    </ul>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3 py-4" aria-label="Loading navigation" aria-busy="true">
        <div className="h-9 w-full animate-pulse rounded-md bg-[#e3eaed]" />
        <div className="h-9 w-full animate-pulse rounded-md bg-[#e3eaed]" />
        <div className="h-9 w-full animate-pulse rounded-md bg-[#e3eaed]" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const items = getFallbackMenuItems();
    return <ul className="flex w-full flex-col gap-1">
        {items.map(item => <li key={item.label}>
            <Link {...item.linkProps} onClick={onNavigate} className="tt-drawer-nav-link">
              <span>{item.label}</span>
            </Link>
          </li>)}
      </ul>;
  }
  const orderedItems = orderMenuItems(menuItems);
  return <MobileRecursiveMenu items={orderedItems} onNavigate={onNavigate} />;
}
function SearchResultsConsumer({
  onSelect
}: {
  onSelect: () => void;
}) {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="space-y-2 p-2" aria-label="Loading search results" aria-busy="true">
        <div className="h-12 w-full animate-pulse rounded-md bg-muted/50" />
        <div className="h-12 w-full animate-pulse rounded-md bg-muted/50" />
      </div>;
  }
  if (error) {
    return <p role="alert" className="px-2 py-4 text-xs text-destructive">
        Unable to load product results.
      </p>;
  }
  if (!products || products.length === 0) {
    return <p className="px-2 py-4 text-xs text-muted-foreground">
        No products found.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {products.slice(0, 6).map(product => <Link key={product.id} to={product.link || `/product/${product.slug}`} onClick={onSelect} className="flex items-center gap-3 rounded-md border border-border bg-card p-2 text-left transition-[border-color,background-color] hover:border-primary/50 hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          {product.featured_image_url ? <div className="size-12 shrink-0 overflow-hidden rounded-md bg-muted">
              <img src={product.featured_image_url} alt={getProductImageAlt(product, product.featured_image_url)} className="h-full w-full object-cover" loading="lazy" decoding="async" />
            </div> : null}

          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-card-foreground">
              {product.title}
            </p>
            <ProductPrice product={product} as="div" className="mt-0.5 font-mono text-xs text-primary" />
          </div>
        </Link>)}
    </div>;
}
function HeaderSearchAction() {
  useProductImageAlts();
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [wpQuery, hasSearchQuery] = useWPQueryForSearch(searchValue);
  return <EcommerceProductsProvider wp_query={wpQuery}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="icon" className="text-foreground hover:bg-secondary/60 hover:text-foreground" aria-label="Search products">
            <Search className="size-4" aria-hidden="true" />
          </Button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-[92vw] max-w-[480px] space-y-3 border-border bg-background p-4 shadow-xl sm:min-w-[420px]">
          <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search products..." className="w-full" />

          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {!hasSearchQuery ? <div className="py-4 text-center">
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Product Search
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Enter a product name to search the catalog.
                </p>
              </div> : <>
                <SearchResultsConsumer onSelect={() => setIsOpen(false)} />

                <div className="flex items-center justify-between border-t border-border pt-2">
                  <Link to={buildProductSearchUrl(searchValue)} onClick={() => setIsOpen(false)} className="text-xs font-medium text-primary hover:underline">
                    View all results
                  </Link>

                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Research Use Only
                  </span>
                </div>
              </>}
          </div>
        </PopoverContent>
      </Popover>
    </EcommerceProductsProvider>;
}
export interface HeaderProps {
  /** When true the access popup never opens (e.g. on FAQ or Contact pages). */
  publicPage?: boolean;
}
export default function Header({
  publicPage = false
}: HeaderProps) {
  const accountUrl = useWooAccountUrl();
  const shouldReduceMotion = useReducedMotion();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [gateLoading, setGateLoading] = useState(!publicPage);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoggedInUser, setIsLoggedInUser] = useState(false);
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authSuccessMessage, setAuthSuccessMessage] = useState("");
  const [authFeedbackVersion, setAuthFeedbackVersion] = useState(0);
  const alertBoxRef = useRef<HTMLDivElement>(null);
  const statusBoxRef = useRef<HTMLDivElement>(null);
  const gateOverlayRef = useRef<HTMLDivElement>(null);
  const [viewportStyle, setViewportStyle] = useState<React.CSSProperties>({});
  const [ageChecked, setAgeChecked] = useState(false);
  const [researchChecked, setResearchChecked] = useState(false);
  const passwordRequirements = [{
    label: "At least 8 characters",
    valid: password.length >= 8
  }, {
    label: "No more than 64 characters",
    valid: password.length > 0 && password.length <= 64
  }, {
    label: "Contains an uppercase letter",
    valid: /[A-Z]/.test(password)
  }, {
    label: "Contains a lowercase letter",
    valid: /[a-z]/.test(password)
  }, {
    label: "Contains a number",
    valid: /[0-9]/.test(password)
  }, {
    label: "Contains a special character",
    valid: /[^A-Za-z0-9]/.test(password)
  }];

  /*
   * ACCESS GATE
   * When publicPage is true, showGate stays false and gateLoading is false right away.
   * Background login check still runs so the cart drawer and account buttons function properly.
   */
  useEffect(() => {
    let active = true;
    async function evaluateAccess() {
      if (publicPage) {
        setShowGate(false);
        setGateLoading(false);
        try {
          const userResult = await auth.getCurrentUser();
          if (active) {
            setIsLoggedInUser(Boolean(userResult?.success && userResult?.data));
          }
        } catch {
          if (active) {
            setIsLoggedInUser(false);
          }
        }
        return;
      }
      const storedAge = safeLocalStorageGet("tek_tides_age_verified") === "true";
      const storedResearch = safeLocalStorageGet("tek_tides_research_acknowledged") === "true";
      const gateCompletedThisSession = safeSessionStorageGet("tek_tides_gate_completed") === "true";
      if (gateCompletedThisSession && storedAge && storedResearch) {
        setShowGate(false);
        setGateLoading(false);
        try {
          const userResult = await auth.getCurrentUser();
          if (active) {
            setIsLoggedInUser(Boolean(userResult?.success && userResult?.data));
          }
        } catch {
          if (active) {
            setIsLoggedInUser(false);
          }
        }
        return;
      }
      setGateLoading(true);
      let loggedIn = false;
      try {
        const userResult = await auth.getCurrentUser();
        loggedIn = Boolean(userResult?.success && userResult?.data);
      } catch {
        loggedIn = false;
      }
      if (!active) {
        return;
      }
      setIsLoggedInUser(loggedIn);
      if (!storedAge || !storedResearch) {
        setCurrentStep(1);
        setShowGate(true);
      } else if (!loggedIn) {
        setCurrentStep(2);
        setShowGate(true);
      } else {
        safeSessionStorageSet("tek_tides_gate_completed", "true");
        setShowGate(false);
      }
      setGateLoading(false);
    }
    void evaluateAccess();
    return () => {
      active = false;
    };
  }, [publicPage]);

  /*
   * Robust iOS Safari scroll lock and touchmove containment while the gate is open.
   */
  useEffect(() => {
    if (!showGate || typeof window === "undefined" || typeof document === "undefined") {
      return;
    }
    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    const bodyStyle = document.body.style;
    const htmlStyle = document.documentElement.style;
    const prevBodyPosition = bodyStyle.position;
    const prevBodyTop = bodyStyle.top;
    const prevBodyLeft = bodyStyle.left;
    const prevBodyWidth = bodyStyle.width;
    const prevBodyOverflow = bodyStyle.overflow;
    const prevHtmlOverflow = htmlStyle.overflow;
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.left = "0px";
    bodyStyle.width = "100%";
    bodyStyle.overflow = "hidden";
    htmlStyle.overflow = "hidden";
    const handleTouchMove = (event: TouchEvent) => {
      const scrollEl = gateOverlayRef.current;
      if (!scrollEl) {
        event.preventDefault();
        return;
      }
      if (!scrollEl.contains(event.target as Node)) {
        event.preventDefault();
      }
    };
    document.addEventListener("touchmove", handleTouchMove, {
      passive: false
    });
    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      bodyStyle.position = prevBodyPosition;
      bodyStyle.top = prevBodyTop;
      bodyStyle.left = prevBodyLeft;
      bodyStyle.width = prevBodyWidth;
      bodyStyle.overflow = prevBodyOverflow;
      htmlStyle.overflow = prevHtmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [showGate]);

  /*
   * Track window.visualViewport height and offsetTop so the gate overlay stays
   * pinned and covers the entire visible area as the software keyboard appears/resizes.
   */
  useEffect(() => {
    if (!showGate || typeof window === "undefined") {
      return;
    }
    const updateViewportBounds = () => {
      const vv = window.visualViewport;
      if (vv) {
        const topPx = Math.round(vv.offsetTop);
        const heightPx = Math.round(vv.height);
        setViewportStyle({
          ["--tt-gate-top" as any]: `${topPx}px`,
          ["--tt-gate-height" as any]: `${heightPx}px`,
          top: `${topPx}px`,
          height: `${heightPx}px`
        });
      } else {
        setViewportStyle({
          ["--tt-gate-top" as any]: "0px",
          ["--tt-gate-height" as any]: "100dvh",
          top: "0px",
          height: "100dvh"
        });
      }
    };
    updateViewportBounds();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", updateViewportBounds);
      vv.addEventListener("scroll", updateViewportBounds);
    }
    window.addEventListener("resize", updateViewportBounds);
    window.addEventListener("orientationchange", updateViewportBounds);
    return () => {
      if (vv) {
        vv.removeEventListener("resize", updateViewportBounds);
        vv.removeEventListener("scroll", updateViewportBounds);
      }
      window.removeEventListener("resize", updateViewportBounds);
      window.removeEventListener("orientationchange", updateViewportBounds);
    };
  }, [showGate]);

  /*
   * Scroll focused input/textarea inside the gate into center view on focus
   * and re-center on visualViewport resize while an input remains focused.
   */
  useEffect(() => {
    if (!showGate || typeof window === "undefined") {
      return;
    }
    let timer: number | null = null;
    const scrollFocusedElement = () => {
      const activeEl = document.activeElement as HTMLElement | null;
      if (!activeEl || !gateOverlayRef.current) return;
      const tag = activeEl.tagName.toLowerCase();
      if ((tag === "input" || tag === "textarea") && gateOverlayRef.current.contains(activeEl)) {
        activeEl.scrollIntoView({
          behavior: shouldReduceMotion ? "auto" : "smooth",
          block: "center"
        });
      }
    };
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || !gateOverlayRef.current) return;
      const tag = target.tagName ? target.tagName.toLowerCase() : "";
      if ((tag === "input" || tag === "textarea") && gateOverlayRef.current.contains(target)) {
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(scrollFocusedElement, 250);
      }
    };
    const vv = window.visualViewport;
    const handleViewportResize = () => {
      scrollFocusedElement();
    };
    document.addEventListener("focusin", handleFocusIn);
    if (vv) {
      vv.addEventListener("resize", handleViewportResize);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
      document.removeEventListener("focusin", handleFocusIn);
      if (vv) {
        vv.removeEventListener("resize", handleViewportResize);
      }
    };
  }, [showGate, shouldReduceMotion]);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /*
   * Keep feedback messages (errors & success) scrolled into view
   * even when errors repeat identically.
   */
  useEffect(() => {
    if (!authError && !authSuccessMessage) {
      return;
    }
    const target = authError ? alertBoxRef.current : statusBoxRef.current;
    if (target) {
      target.scrollIntoView({
        block: "center",
        behavior: shouldReduceMotion ? "auto" : "smooth"
      });
    }
  }, [authError, authSuccessMessage, authFeedbackVersion, shouldReduceMotion]);

  /*
   * Hide WordPress Side Cart WooCommerce plugin elements unconditionally.
   * Strip leftover plugin classes on body so it cannot disrupt page state.
   */
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.body.classList.remove("xoo-wsc-cart-active");
    const observer = new MutationObserver(() => {
      if (document.body.classList.contains("xoo-wsc-cart-active")) {
        document.body.classList.remove("xoo-wsc-cart-active");
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"]
    });
    return () => {
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    if (publicPage) {
      setAccessGateState({
        loading: false,
        gateOpen: false
      });
    } else {
      setAccessGateState({
        loading: gateLoading,
        gateOpen: showGate
      });
    }
  }, [publicPage, gateLoading, showGate]);
  const advanceAfterAccessCheck = () => {
    if (!ageChecked || !researchChecked) {
      return;
    }
    safeLocalStorageSet("tek_tides_age_verified", "true");
    safeLocalStorageSet("tek_tides_research_acknowledged", "true");
    if (isLoggedInUser) {
      safeSessionStorageSet("tek_tides_gate_completed", "true");
      setShowGate(false);
    } else {
      setCurrentStep(2);
    }
  };
  const handleRegister = async (event: React.FormEvent) => {
    event.preventDefault();
    setAuthError("");
    setAuthSuccessMessage("");
    if (!email || !password || !confirmPassword) {
      setAuthError("All fields are required.");
      setAuthFeedbackVersion(v => v + 1);
      return;
    }
    if (password !== confirmPassword) {
      setAuthError("Passwords do not match.");
      setAuthFeedbackVersion(v => v + 1);
      return;
    }
    const isPasswordValid = auth.validatePassword(password);
    if (!isPasswordValid) {
      setAuthError("Password must be 8–64 characters and include an uppercase letter, lowercase letter, number, and special character.");
      setAuthFeedbackVersion(v => v + 1);
      return;
    }
    setAuthLoading(true);
    try {
      const result = await auth.register({
        email,
        password,
        username: email
      });
      if (result?.success) {
        setAuthSuccessMessage("Account created. Please sign in to continue.");
        setAuthFeedbackVersion(v => v + 1);
        setAuthMode("signin");
        setPassword("");
        setConfirmPassword("");
      } else {
        setAuthError(result?.error?.message || "Registration failed. Please try again.");
        setAuthFeedbackVersion(v => v + 1);
      }
    } catch (error: any) {
      setAuthError(error?.message || "Unable to create your account. Please try again.");
      setAuthFeedbackVersion(v => v + 1);
    } finally {
      setAuthLoading(false);
    }
  };
  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setAuthError("");
    setAuthSuccessMessage("");
    if (!email || !password) {
      setAuthError("Please enter your email and password.");
      setAuthFeedbackVersion(v => v + 1);
      return;
    }
    setAuthLoading(true);
    try {
      const result = await auth.login(email, password, rememberMe);
      if (result?.success) {
        let confirmedLogin = false;
        try {
          const currentUser = await auth.getCurrentUser();
          confirmedLogin = Boolean(currentUser?.success && currentUser?.data);
        } catch {
          confirmedLogin = true;
        }
        if (!confirmedLogin) {
          setAuthError("Sign-in could not be confirmed. Please try again.");
          setAuthFeedbackVersion(v => v + 1);
          return;
        }
        setIsLoggedInUser(true);
        const storedAge = safeLocalStorageGet("tek_tides_age_verified") === "true";
        const storedResearch = safeLocalStorageGet("tek_tides_research_acknowledged") === "true";
        if (storedAge && storedResearch) {
          safeSessionStorageSet("tek_tides_gate_completed", "true");
          setShowGate(false);
        } else {
          setCurrentStep(1);
        }
      } else {
        setAuthError(result?.error?.message || "Invalid email or password.");
        setAuthFeedbackVersion(v => v + 1);
      }
    } catch (error: any) {
      setAuthError(error?.message || "Sign in failed. Please try again.");
      setAuthFeedbackVersion(v => v + 1);
    } finally {
      setAuthLoading(false);
    }
  };
  return <>
      {!gateLoading && showGate && !publicPage && <div ref={gateOverlayRef} style={viewportStyle} className="tt-gate-overlay fixed inset-x-0 z-[100] flex flex-col items-center bg-[#08111a]" role="dialog" aria-modal="true" aria-labelledby="access-gate-title">
          <div className="tt-gate-dialog relative my-auto w-full max-w-lg shrink-0 overflow-hidden rounded-[22px] border border-[#24475a] bg-[#101923] shadow-[0_30px_90px_rgba(0,0,0,0.45)]">
            {/* Light brand panel keeps the standard TEK TIDES logo crisp and readable. */}
            <div className="relative border-b border-[#dbe6eb] bg-[#f8fafb] px-7 py-7 text-center sm:px-9">
              <img alt="TEK TIDES LABS" className="mx-auto h-auto w-full max-w-[245px] object-contain" loading="eager" decoding="async" src={TEK_TIDES_LOGO} />
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#bcd8e5] bg-[#eaf5fa] px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#087db3]">
                <ShieldCheck className="size-3.5" aria-hidden="true" />
                Access Verification
              </div>
            </div>

            <div className="p-6 sm:p-8">
              <div className="mb-7 flex items-center gap-3" aria-label={`Access step ${currentStep} of 2`}>
                {[1, 2].map(stepNumber => {
              const passed = stepNumber < currentStep;
              const current = stepNumber === currentStep;
              return <div key={stepNumber} className="flex flex-1 items-center gap-2">
                      <div className={`h-1.5 flex-1 rounded-full transition-colors ${passed || current ? "bg-[#159fdf]" : "bg-white/10"}`} />
                      <span className={`text-[9px] font-semibold ${current ? "text-[#55c8f7]" : passed ? "text-white/70" : "text-white/30"}`}>0{stepNumber}</span>
                    </div>;
            })}
              </div>

              {currentStep === 1 && <div className="space-y-5">
                  <div className="text-center">
                    <h2 id="access-gate-title" className="text-xl font-semibold tracking-[-0.025em] text-white">
                      Verify Access
                    </h2>
                    <p className="mt-2 text-xs leading-5 text-[#9eabb3]">
                      Confirm the requirements below to continue.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <label className="flex cursor-pointer select-none items-start gap-3 rounded-xl border border-white/10 bg-white/[0.035] p-4 transition-colors hover:border-[#159fdf]/40">
                      <input type="checkbox" checked={ageChecked} onChange={event => setAgeChecked(event.target.checked)} className="mt-0.5 size-4 shrink-0 accent-[#159fdf]" />
                      <span className="text-sm font-medium leading-relaxed text-white">
                        I confirm that I am 21 years of age or older.
                      </span>
                    </label>

                    <label className="flex cursor-pointer select-none items-start gap-3 rounded-xl border border-white/10 bg-white/[0.035] p-4 transition-colors hover:border-[#159fdf]/40">
                      <input type="checkbox" checked={researchChecked} onChange={event => setResearchChecked(event.target.checked)} className="mt-0.5 size-4 shrink-0 accent-[#159fdf]" />
                      <span className="text-xs font-medium leading-relaxed text-white sm:text-sm">
                        I acknowledge that products offered by TEK TIDES LABS are supplied strictly for research use only and are not intended for diagnostic, clinical, therapeutic, human, or veterinary applications.
                      </span>
                    </label>
                  </div>

                  <Button type="button" className="h-11 w-full bg-[#168fc4] text-white hover:bg-[#117eae]" disabled={!ageChecked || !researchChecked} onClick={advanceAfterAccessCheck}>
                    {isLoggedInUser ? "Enter TEK TIDES LABS" : "Continue to Account"}
                    <ArrowRight className="ml-1 size-4" aria-hidden="true" />
                  </Button>
                </div>}

              {currentStep === 2 && <div className="space-y-5">
                  <div className="text-center">
                    <h2 id="access-gate-title" className="text-xl font-semibold tracking-[-0.025em] text-white">
                      Account Access
                    </h2>
                    <p className="mt-2 text-xs leading-5 text-[#9eabb3]">
                      Sign in or create an account to enter the catalog.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 rounded-lg border border-white/10 bg-black/20 p-1">
                    <Button type="button" variant={authMode === "signin" ? "default" : "ghost"} className="h-11 text-sm font-medium" onClick={() => {
                setAuthMode("signin");
                setAuthError("");
                setAuthSuccessMessage("");
              }}>
                      Sign In
                    </Button>
                    <Button type="button" variant={authMode === "signup" ? "default" : "ghost"} className="h-11 text-sm font-medium" onClick={() => {
                setAuthMode("signup");
                setAuthError("");
                setAuthSuccessMessage("");
              }}>
                      Create Account
                    </Button>
                  </div>

                  {authSuccessMessage && <div ref={statusBoxRef} role="status" className="flex items-start gap-2 rounded-lg border border-[#159fdf]/30 bg-[#159fdf]/10 p-3 text-xs text-[#55c8f7]">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                      {authSuccessMessage}
                    </div>}

                  {authError && <div ref={alertBoxRef} role="alert" className="flex items-start gap-2 rounded-lg border border-red-400/40 bg-red-500/10 p-3 text-xs text-red-300 break-words">
                      <AlertCircle className="mt-0.5 size-4 shrink-0 text-red-300" aria-hidden="true" />
                      <span className="flex-1 break-words">{authError}</span>
                    </div>}

                  {authMode === "signin" ? <form onSubmit={handleLogin} className="space-y-4">
                      <div className="space-y-1.5">
                        <label htmlFor="gate-login-email" className="text-xs font-medium text-[#aab6bd]">Email Address</label>
                        <input id="gate-login-email" type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} className="flex h-11 w-full rounded-lg border border-white/15 bg-[#0b131c] px-3 text-base sm:text-sm text-white outline-none focus:border-[#159fdf] focus:ring-1 focus:ring-[#159fdf]" />
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="gate-login-password" className="text-xs font-medium text-[#aab6bd]">Password</label>
                        <div className="relative">
                          <input id="gate-login-password" type={showPassword ? "text" : "password"} autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} className="flex h-11 w-full rounded-lg border border-white/15 bg-[#0b131c] px-3 pr-11 text-base sm:text-sm text-white outline-none focus:border-[#159fdf] focus:ring-1 focus:ring-[#159fdf]" />
                          <Button type="button" variant="ghost" size="icon" onClick={() => setShowPassword(value => !value)} className="absolute right-0 top-0 h-full w-11 text-[#9eabb3] hover:bg-transparent hover:text-white" aria-label={showPassword ? "Hide password" : "Show password"}>
                            {showPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap">
                        <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2">
                          <input type="checkbox" checked={rememberMe} onChange={event => setRememberMe(event.target.checked)} className="size-4 accent-[#159fdf]" />
                          <span className="text-xs text-[#c0c9ce]">Remember me</span>
                        </label>
                        <Link href="https://tektideslabs.com/wp-login.php?action=lostpassword" className="inline-flex min-h-[44px] items-center px-1 text-xs text-[#8f9ca4] transition-colors hover:text-[#55c8f7] hover:underline">Forgot password?</Link>
                      </div>

                      <Button type="submit" className="h-11 w-full bg-[#168fc4] text-white hover:bg-[#117eae]" disabled={authLoading}>
                        {authLoading ? <>
                            <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" />
                            Signing In...
                          </> : "Sign In & Enter"}
                      </Button>
                    </form> : <form onSubmit={handleRegister} className="space-y-4">
                      <div className="space-y-1.5">
                        <label htmlFor="gate-register-email" className="text-xs font-medium text-[#aab6bd]">Email Address</label>
                        <input id="gate-register-email" type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} className="flex h-11 w-full rounded-lg border border-white/15 bg-[#0b131c] px-3 text-base sm:text-sm text-white outline-none focus:border-[#159fdf] focus:ring-1 focus:ring-[#159fdf]" />
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="gate-register-password" className="text-xs font-medium text-[#aab6bd]">Password</label>
                        <div className="relative">
                          <input id="gate-register-password" type={showPassword ? "text" : "password"} autoComplete="new-password" required value={password} onChange={event => setPassword(event.target.value)} onFocus={() => setPasswordFocused(true)} onBlur={() => setPasswordFocused(false)} className="flex h-11 w-full rounded-lg border border-white/15 bg-[#0b131c] px-3 pr-11 text-base sm:text-sm text-white outline-none focus:border-[#159fdf] focus:ring-1 focus:ring-[#159fdf]" />
                          <Button type="button" variant="ghost" size="icon" onClick={() => setShowPassword(value => !value)} className="absolute right-0 top-0 h-full w-11 text-[#9eabb3] hover:bg-transparent hover:text-white" aria-label={showPassword ? "Hide password" : "Show password"}>
                            {showPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                          </Button>
                        </div>

                        {(passwordFocused || password.length > 0) && <div className="mt-2 space-y-1.5 rounded-lg border border-white/10 bg-black/20 p-2.5">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8f9ca4]">Password Requirements</p>
                            <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                              {passwordRequirements.map(requirement => <li key={requirement.label} className={`flex items-center gap-1.5 text-[11px] ${requirement.valid ? "text-emerald-400" : "text-[#8f9ca4]"}`}>
                                  {requirement.valid ? <Check className="size-3.5 shrink-0" aria-hidden="true" /> : <X className="size-3.5 shrink-0" aria-hidden="true" />}
                                  {requirement.label}
                                </li>)}
                            </ul>
                          </div>}
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="gate-confirm-password" className="text-xs font-medium text-[#aab6bd]">Confirm Password</label>
                        <div className="relative">
                          <input id="gate-confirm-password" type={showConfirmPassword ? "text" : "password"} autoComplete="new-password" required value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} className="flex h-11 w-full rounded-lg border border-white/15 bg-[#0b131c] px-3 pr-11 text-base sm:text-sm text-white outline-none focus:border-[#159fdf] focus:ring-1 focus:ring-[#159fdf]" />
                          <Button type="button" variant="ghost" size="icon" onClick={() => setShowConfirmPassword(value => !value)} className="absolute right-0 top-0 h-full w-11 text-[#9eabb3] hover:bg-transparent hover:text-white" aria-label={showConfirmPassword ? "Hide password" : "Show password"}>
                            {showConfirmPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                          </Button>
                        </div>
                      </div>

                      <Button type="submit" className="h-11 w-full bg-[#168fc4] text-white hover:bg-[#117eae]" disabled={authLoading}>
                        {authLoading ? <>
                            <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" />
                            Creating Account...
                          </> : "Create Account"}
                      </Button>
                    </form>}

                  <button type="button" onClick={() => setCurrentStep(1)} className="mx-auto flex min-h-[44px] items-center justify-center px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#7f8d95] transition-colors hover:text-[#55c8f7]">
                    Back to access verification
                  </button>
                </div>}
            </div>
          </div>
        </div>}

      <header id="header" data-nav="light" className={`sticky top-0 z-50 w-full border-b transition-[background-color,box-shadow,border-color] duration-200 ${isScrolled ? "border-[#dfe7eb] bg-[#f8fafb]/95 shadow-[0_8px_30px_rgba(10,18,24,0.06)] backdrop-blur-xl" : "border-[#e5ecef] bg-[#f8fafb]"}`}>
        {/* Slim announcement bar */}
        <div className="bg-[#0f7fb3]">
          <div className="mx-auto max-w-[1440px] px-3 py-1.5 text-center sm:px-6 lg:px-8">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white sm:text-[11px] sm:tracking-[0.14em] leading-snug">
              25% OFF YOUR FIRST ORDER • USE CODE WELCOME25
            </span>
          </div>
        </div>

        <div className="border-b border-[#d1d5db] bg-[#e5e7eb]">
          <div className="mx-auto max-w-[1440px] px-3 py-0.5 text-center sm:px-6 sm:py-1 lg:px-8">
            <p className="text-center text-[10px] font-normal leading-snug text-[#2b2f33] sm:text-[11px] sm:font-medium">
              For laboratory research use only. Not for human or veterinary use.
            </p>
          </div>
        </div>

        {/* Main navigation */}
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-[68px] items-center justify-between gap-4">
            {/* Logo */}
            <div className="flex shrink-0 items-center">
              <Link to="/" aria-label="TEK TIDES LABS home" className="block">
                <img alt="TEK TIDES LABS" loading="eager" decoding="async" src={TEK_TIDES_LOGO} className="h-auto w-[150px] object-contain sm:w-[165px] md:w-[178px]" />
              </Link>
            </div>

            {/* Desktop navigation */}
            <div className="hidden flex-1 items-center justify-center md:flex">
              <div className="[&_a]:!text-[#172126] [&_button]:!text-[#172126] [&_a:hover]:!text-[#087db3] [&_button:hover]:!text-[#087db3]">
                <MenuProvider menu_id="20">
                  <DesktopMenuConsumer />
                </MenuProvider>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              {isLoggedInUser && <div className="[&_button]:!text-[#172126] [&_button:hover]:!bg-[#e9f1f4]">
                  <HeaderSearchAction />
                </div>}

              <Button asChild variant="ghost" size="icon" className="rounded-full text-[#172126] hover:bg-[#e9f1f4] hover:text-[#087db3]">
                <Link to={accountUrl} aria-label="My account">
                  <User className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <HeaderCart enabled={!gateLoading && !showGate && isLoggedInUser || publicPage && isLoggedInUser} />

              {/* Mobile navigation */}
              <div className="md:hidden">
                <DialogPrimitive.Root open={isMobileOpen} onOpenChange={setIsMobileOpen}>
                  <DialogPrimitive.Trigger asChild>
                    <Button type="button" variant="ghost" size="icon" className="rounded-full text-[#172126] hover:bg-[#e9f1f4] hover:text-[#087db3]" aria-label="Open navigation menu">
                      <Menu className="size-5" aria-hidden="true" />
                    </Button>
                  </DialogPrimitive.Trigger>

                  <DialogPrimitive.Portal>
                    <DialogPrimitive.Overlay className="tt-mobile-drawer-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

                    <DialogPrimitive.Content style={{
                    colorScheme: "light"
                  }} className="tt-mobile-drawer data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500">
                      <DialogPrimitive.Close className="tt-mobile-drawer-close" aria-label="Close navigation menu">
                        <X className="size-5" aria-hidden="true" />
                        <span className="sr-only">Close</span>
                      </DialogPrimitive.Close>

                      <div className="space-y-6">
                        <div className="border-b border-[#dce6ea] pb-4 text-left">
                          <DialogPrimitive.Title asChild>
                            <div>
                              <Link to="/" onClick={() => setIsMobileOpen(false)} aria-label="TEK TIDES LABS home" className="block">
                                <img src={TEK_TIDES_LOGO} alt="TEK TIDES LABS" className="h-auto w-[170px] object-contain" loading="lazy" decoding="async" />
                              </Link>
                            </div>
                          </DialogPrimitive.Title>

                          <DialogPrimitive.Description className="tt-drawer-description mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]">
                            Research-Use Products
                          </DialogPrimitive.Description>
                        </div>

                        <div className="max-h-[calc(100dvh-230px)] overflow-y-auto py-2">
                          <MenuProvider menu_id="20">
                            <MobileMenuConsumer onNavigate={() => setIsMobileOpen(false)} />
                          </MenuProvider>
                        </div>
                      </div>

                      <div className="space-y-3 border-t border-[#dce6ea] pt-4">
                        <div className="tt-drawer-notice-card">
                          <p className="tt-drawer-notice-title">
                            Research Use Only
                          </p>

                          <p className="tt-drawer-notice-body mt-1">
                            Products offered by TEK TIDES LABS are supplied
                            strictly for research use only and are not intended
                            for diagnostic, clinical, therapeutic, human, or
                            veterinary applications.
                          </p>
                        </div>

                        <Link to={accountUrl} onClick={() => setIsMobileOpen(false)} className="tt-drawer-account-btn">
                          My Account
                        </Link>
                      </div>
                    </DialogPrimitive.Content>
                  </DialogPrimitive.Portal>
                </DialogPrimitive.Root>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>;
}